import { GlobalSettings } from '../types';

export interface SensitiveDataResult {
    detected: boolean;
    type: 'credit_card' | 'ssn' | 'password' | 'api_key' | null;
}

// Shannon entropy calculation
export function calculateEntropy(str: string): number {
    const len = str.length;
    if (len === 0) return 0;

    const frequencies: Record<string, number> = {};
    for (let i = 0; i < len; i++) {
        const char = str[i];
        frequencies[char] = (frequencies[char] || 0) + 1;
    }

    let entropy = 0;
    for (const char in frequencies) {
        const p = frequencies[char] / len;
        entropy -= p * Math.log2(p);
    }

    return entropy;
}

export function detectSensitiveInfo(text: string, settings: GlobalSettings | null): SensitiveDataResult {
    if (!text || !settings) return { detected: false, type: null };

    // 1. Credit Card Check
    // Matches 13-16 consecutive digits with optional spaces or dashes in between
    if (settings.warn_on_credit_card !== false) {
        const ccRegex = /\b(?:\d[ -]*?){13,16}\b/;
        if (ccRegex.test(text)) {
            return { detected: true, type: 'credit_card' };
        }
    }

    // 2. SSN Check
    // Matches standard US SSN format XXX-XX-XXXX or XXXXXXXXX
    if (settings.warn_on_ssn !== false) {
        const ssnRegex = /\b(?!000|666)[0-8][0-9]{2}[- ]?(?!00)[0-9]{2}[- ]?(?!0000)[0-9]{4}\b/;
        if (ssnRegex.test(text)) {
            return { detected: true, type: 'ssn' };
        }
    }

    // 3. Password / API Key Check
    if (settings.warn_on_password !== false) {
        // 3a. Keyword context check
        const passwordKeywordRegex = /(?:password|pass|pwd|secret)\s*[:=]\s*(\S+)/i;
        if (passwordKeywordRegex.test(text)) {
            return { detected: true, type: 'password' };
        }

        // 3b. High entropy string check (for randomly generated passwords/API keys)
        // Split by whitespace and check words that are at least 10 chars long
        const words = text.split(/\s+/);
        for (const word of words) {
            // Ignore URLs and URIs to prevent false positives
            // Catches: https://..., http://..., www.domain.com/path, domain.com/path/etc
            if (/^https?:\/\//i.test(word) || word.includes('://') || /^www\./i.test(word) || /^[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\/|\?|#)/i.test(word)) {
                continue;
            }

            if (word.length >= 10 && word.length <= 64) {
                const entropy = calculateEntropy(word);
                // A typical English word has low entropy per character compared to a random string.
                // A 10+ char random string of mixed case/numbers/symbols often exceeds an entropy of 3.8 to 4.5.
                if (entropy > 4.2) {
                    return { detected: true, type: 'api_key' }; // Grouping high entropy under api_key/password
                }
            }
        }
    }


    return { detected: false, type: null };
}
